import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';

import { cn } from '@/shared/lib/cn';

export type ToastTone = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
  durationMs?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastEntry extends ToastOptions {
  id: number;
}

interface ToastContextValue {
  toast: (options: ToastOptions) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_VISIBLE = 3;

const TONE_STYLES: Record<ToastTone, { icon: React.ReactNode; accent: string }> = {
  success: { icon: <CheckCircle2 className="h-4 w-4 text-success" />, accent: 'border-l-success' },
  error: { icon: <XCircle className="h-4 w-4 text-danger" />, accent: 'border-l-danger' },
  warning: { icon: <AlertTriangle className="h-4 w-4 text-warning" />, accent: 'border-l-warning' },
  info: { icon: <Info className="h-4 w-4 text-info" />, accent: 'border-l-info' },
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++;
      // Cap the stack: more than three at once is noise, not feedback.
      setToasts((current) => [...current.slice(-(MAX_VISIBLE - 1)), { ...options, id }]);
      const duration = options.durationMs ?? (options.tone === 'error' ? 8000 : 4500);
      window.setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* aria-live so a screen reader hears results it cannot see. */}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
        role="region"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((entry) => {
          const tone = TONE_STYLES[entry.tone ?? 'info'];
          return (
            <div
              key={entry.id}
              className={cn(
                'pointer-events-auto flex animate-slide-up items-start gap-2.5 rounded-lg border border-l-2 border-border bg-surface-raised p-3 shadow-lg',
                tone.accent,
              )}
            >
              <span className="mt-0.5 shrink-0">{tone.icon}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-fg">{entry.title}</p>
                {entry.description && <p className="mt-0.5 text-xs text-fg-muted">{entry.description}</p>}
                {entry.action && (
                  <button
                    type="button"
                    onClick={() => {
                      entry.action?.onClick();
                      dismiss(entry.id);
                    }}
                    className="mt-1.5 cursor-pointer text-xs font-medium text-accent hover:underline"
                  >
                    {entry.action.label}
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => dismiss(entry.id)}
                aria-label={t('common.close')}
                className="shrink-0 cursor-pointer rounded p-0.5 text-fg-subtle transition-colors hover:bg-surface-hover hover:text-fg"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside <ToastProvider>');
  return context;
}
